import React, { Component } from "react";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import uuid from "uuid";
import { Route, Switch } from "react-router-dom";
import { Alert } from "reactstrap";
import { withRouter } from "react-router";

import routes from "../navigation/routes";
import { PrivateRoute } from "../components/common";
import AppContext from "../components/context/AppContext";
import {
  arrDateToDateObjInLocalTime,
  currencyFormat,
  stringToNumberFormat
} from "../services/dataConversion";
import { createPdfFile } from "../services/createPdf";
import { OrderStatusEnum } from "../config/orderStatusEnum";

import fetchData from "../helpers/fetchData";

import {
  ACCOMMODATION_PAYMENT,
  ACCOMMODATION_TYPE,
  ADDITIONAL_COST_TYPES,
  PERSONAL_CAR_EUR_PER_KILOMETER,
  COMPANY_CAR_EUR_PER_KILOMETER,
  COUNTRIES_LIST,
  PAYMENT_METHODS,
  TRIP_PURPOSE_LIST,
  CAR_LIST,
  VEHICLES_LIST
} from "../const";
import { ENDPOINT, ROLES } from "../config";
import {
  getUserInfo,
  isAdminRole,
  isLoggedIn,
  setUserId
} from "../services/userInfo";
import { URL } from "../navigation/paths";
import { CountryWages } from "../countryWagesEnum";
import {
  findExpenseDataCurrentExpenseData,
  getCountryValuesFromCountriesList
} from "./globalStateWrapper-helper";

const todayDate = moment();
let timeoutId = null;
const TIMEOUT_TIME = 1300;

const createDefaultExpenseCard = id => ({
  id,
  countryDropdownOpen: false,
  dropdownCountryValue: COUNTRIES_LIST[1].name,
  accommodationPaymentDropdownOpen: false,
  dropdownAccommodationPaymentValue: ACCOMMODATION_PAYMENT[1].name,
  accommodationTypeDropdownOpen: false,
  dropdownAccommodationTypeValue: ACCOMMODATION_TYPE[0].name,
  wageDepartureDate: todayDate,
  wageReturnDate: todayDate,
  additionalComment: "",
  countryWageValue: CountryWages.DEU,
  accommodationPaymentCoefficientValue: ACCOMMODATION_PAYMENT[1].coefficient,
  accommodationTypeCoefficientValue: ACCOMMODATION_TYPE[0].coefficient,
  totalHours: 0,
  fullWages: 0,
  itemWageAmount: 0
});

const createDefaultAdditionalExpenseCard = id => ({
  id,
  date: todayDate,
  type: ADDITIONAL_COST_TYPES[0].name,
  typeDropdownOpen: false,
  comment: "",
  currencyRate: 1,
  currencyDropdownOpen: false,
  currencyDropdownValue: "EUR",
  paymentMethodsList: PAYMENT_METHODS,
  paymentMethodValue: PAYMENT_METHODS[1].name,
  paymentMethodDropdownOpen: false,
  amount: 0,
  amountEUR: 0,
  unadmitted: 0,
  unadmittedEUR: 0
});

const generateDefaultState = () => ({
  currentRoute: null,
  travelRecords: [],
  travelRecordsCopy: [],
  orderFlags: { orderNumber: -1, departureDate: -1, paymentAmount: -1 },
  error: undefined,
  users: [],
  travelExpense: [],
  travelIdData: {
    orderNumber: null,
    yearDropDownOpen: false,
    yearDropDownValue: new Date().getFullYear()
  },
  personalData: {
    firstName: "",
    lastName: "",
    profession: "",
    workingPosition: ""
  },
  travelInfoData: {
    departureDate: todayDate,
    returnDate: todayDate,
    downPayment: 0,
    documentPriorDate: todayDate.subtract(1, "days"),
    duration: 0,
    destinationCity: "",
    destinationCountry: "",
    vehicleDropDownOpen: false,
    vehicleDropDownValue: VEHICLES_LIST[0].name,
    tripPurposeComment: "",
    tripPurposeGroup: TRIP_PURPOSE_LIST[0].name,
    tripPurposeGroupDropdownOpen: false,
    carMake: "",
    registrationPlateNumber: "",
    kilometersInitialState: 0,
    kilometersFinalState: 0,
    totalKilometers: 0,
    travelCostValue: 0,
    isTripCanceled: false
  },
  travelExpenseData: {
    expenseList: [createDefaultExpenseCard(uuid.v4())],
    totalWageAmount: 0
  },
  additionalExpenseData: {
    expenseList: [createDefaultAdditionalExpenseCard(uuid.v4())],
    totalAdmittedAmount: 0,
    totalUnadmittedAmount: 0,
    currencyRatesList: []
  },
  totalUnadmittedAmountTemp: 0,
  totalTravelExpenseAmount: 0,
  paymentAmount: 0,
  dayLabel: "dan",
  travelExpenseYearValue: todayDate.year(),
  documentSaved: false,
  hasFile: false,
  orderStatus: OrderStatusEnum.OPEN
});

class GlobalStateWrapper extends Component {
  state = generateDefaultState();

  //same as travelRecords
  setTravelExpenses = (travelExpenses: TravelExpense[]) => {
    this.setState({ travelRecords: travelExpenses });
  };

  componentDidMount() {
    if (isLoggedIn()) {
      getUserInfo()
        .then(x => {
          setUserId(x.id);
          this.setState({ isLoggedInUserAdmin: isAdminRole(x.authorities) });
        })
        .catch(_ => this.setState({ error: "Error while fetching users" }));
    }

    // listen for route change
    this.props.history.listen(({ pathname }) => {
      if (pathname === URL.homePage) {
        return;
      }
      if (!isLoggedIn()) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(_ => {
          this.setState({ error: null });
        }, TIMEOUT_TIME);
        this.setState({ error: "Login to access route" });
        return;
      }
      if (pathname === URL.createTravelExpense) {
        this.setState(generateDefaultState());
        return;
      }

      this.setState({
        error: undefined,
        documentSaved: false,
        currentRoute: pathname
      });
    });
  }

  // *** TRAVELID ***

  componentDidUpdate() {
    // implement documentSaved to false and error to null if input changes
    // EASIEST SOLUTION: you can remove the code from componentDidUpdate and place it on every fetchData
    // remove success message
    if (this.state.documentSaved) {
      window.scrollTo(0, 0);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(_ => {
        this.setState({ documentSaved: false });
      }, TIMEOUT_TIME);
    }
    // remove global error
    if (this.state.error) {
      window.scrollTo(0, 0);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(_ => {
        this.setState({ error: null, documentSaved: null });
      }, TIMEOUT_TIME);
    }
  }

  travelIdToggleYearDropdown = () => {
    this.setState(prevState => ({
      travelIdData: {
        ...prevState.travelIdData,
        yearDropDownOpen: !prevState.travelIdData.yearDropDownOpen
      }
    }));
  };

  // ----------------
  // *** PERSONAL DATA ***

  travelIdChangeYearValue = e => {
    this.setState({
      travelIdData: {
        ...this.state.travelIdData,
        yearDropDownValue: e.currentTarget.textContent
      }
    });
  };

  setHasFile = hasFile => {
    this.setState({
      hasFile: hasFile
    });
  };

  // -----------------
  // *** TRAVEL INFO ***

  personalDataHandleInputChange = e => {
    this.setState({
      personalData: {
        ...this.state.personalData,
        [e.target.name]: e.target.value
      }
    });
  };

  travelInfoHandleDepartureDateChange = date => {
    let newDate = this.state.travelInfoData.returnDate;

    if (date.isSameOrAfter(newDate)) {
      newDate = moment(date).add(1, "minutes");
    }

    let documentPriorDate = todayDate;

    if (moment(date).day() === 1) {
      // if monday
      documentPriorDate = moment(date).subtract(3, "days");
    } else if (moment(date).day() === 2) {
      // if tuesday
      documentPriorDate = moment(date).subtract(4, "days");
    } else {
      documentPriorDate = moment(date).subtract(2, "days");
    }

    this.setState({
      travelInfoData: {
        ...this.state.travelInfoData,
        departureDate: date,
        returnDate: newDate,
        documentPriorDate
      }
    });
  };

  travelInfoHandleReturnDateChange = date => {
    this.setState(
      {
        travelInfoData: {
          ...this.state.travelInfoData,
          returnDate: date
        }
      },
      () => this.calculateTravelDuration()
    );
  };

  calculateTravelDuration = () => {
    const duration = this.state.travelInfoData.returnDate
      .add(1, "seconds")
      .diff(this.state.travelInfoData.departureDate, "days");

    this.setState({
      travelInfoData: {
        ...this.state.travelInfoData,
        duration
      },
      dayLabel: duration > 1 ? "dana" : "dan"
    });
  };

  travelInfoHandleInputChange = async e => {
    const targetName = e.target.name;
    let value = e.target.value;

    if (targetName === "downPayment") {
      this.setState(
        {
          travelInfoData: {
            ...this.state.travelInfoData,
            [e.target.name]: stringToNumberFormat(e.target.value)
          }
        },
        () => this.calculateTotalTravelExpense()
      );
    } else {
      this.setState({
        travelInfoData: {
          ...this.state.travelInfoData,
          [targetName]: value
        }
      });
    }
  };

  travelInfoTripPurposeGroupDropdownToggle = () => {
    this.setState(prevState => ({
      travelInfoData: {
        ...prevState.travelInfoData,
        tripPurposeGroupDropdownOpen: !prevState.travelInfoData
          .tripPurposeGroupDropdownOpen
      },
      documentGenerated: false
    }));
  };

  travelInfoTripPurposeGroupChangeValue = e => {
    this.setState({
      travelInfoData: {
        ...this.state.travelInfoData,
        tripPurposeGroup: e.currentTarget.textContent
      },
      documentGenerated: false
    });
  };

  excludeMainAndAdditionalExpensesFromTotal = () => {
    return new Promise((resolve, reject) => {
      this.setState(
        {
          totalTravelExpenseAmount: 0,
          additionalExpenseData: {
            ...this.state.additionalExpenseData,
            totalUnadmittedAmount: 0
          }
        },
        () => resolve()
      );
    });
  };

  saveUnadmittedAmountToTemp = () => {
    const {
      additionalExpenseData: { totalUnadmittedAmount }
    } = this.state;

    return new Promise((resolve, reject) => {
      this.setState(
        {
          totalUnadmittedAmountTemp: totalUnadmittedAmount
        },
        () => resolve()
      );
    });
  };

  travelInfoToggleVehicleDropdown = () => {
    this.setState(prevState => ({
      travelInfoData: {
        ...prevState.travelInfoData,
        vehicleDropDownOpen: !prevState.travelInfoData.vehicleDropDownOpen
      }
    }));
  };

  travelInfoChangeVehicleValue = e => {
    this.setState({
      travelInfoData: {
        ...this.state.travelInfoData,
        kilometersInitialState: 0,
        kilometersFinalState: 0,
        totalKilometers: 0,
        vehicleDropDownValue: e.currentTarget.textContent,
        travelCostValue: 0
      }
    });
  };

  // ----------------------

  travelInfoCancelTrip = e => {
    const targetName = e.target.name;
    const value = e.target.checked;

    this.setState({
      travelInfoData: {
        ...this.state.travelInfoData,
        [targetName]: value
      }
    });

    if (value) {
      this.setState(
        {
          travelInfoData: {
            ...this.state.travelInfoData,
            downPayment: 0,
            isTripCanceled: value
          },
          travelExpenseData: {
            expenseList: [],
            totalWageAmount: 0
          },
          additionalExpenseData: {
            expenseList: [],
            totalAdmittedAmount: 0,
            totalUnadmittedAmount: 0,
            currencyRatesList: []
          },
          totalUnadmittedAmountTemp: 0,
          totalTravelExpenseAmount: 0,
          paymentAmount: 0,
          dayLabel: "dan",
          travelExpenseYearValue: todayDate.year(),
          documentSaved: false
        },
        this.calculateTotalTravelExpense
      );
    } else {
      this.setState({
        travelInfoData: {
          ...this.state.travelInfoData,
          [targetName]: value
        }
      });
    }
  };

  // *** CAR TRAVEL INFO ***
  carTravelInfoHandleInputChange = e => {
    this.setState({
      travelInfoData: {
        ...this.state.travelInfoData,
        [e.target.name]: e.target.value
      }
    });
  };

  carTravelInfoHandleKilometersChange = e => {
    this.setState(
      {
        travelInfoData: {
          ...this.state.travelInfoData,
          [e.target.name]: e.target.value
        }
      },
      () => this.calculateTotalKilometers()
    );
  };

  calculateTotalKilometers = async () => {
    const {
      travelInfoData: {
        kilometersFinalState,
        kilometersInitialState,
        vehicleDropDownValue
      }
    } = this.state;

    if (kilometersFinalState >= kilometersInitialState) {
      const totalKm = await this.updateTotalKilometers(
        kilometersFinalState,
        kilometersInitialState
      );
      const pricePerKilometer =
        vehicleDropDownValue === CAR_LIST[0].name
          ? PERSONAL_CAR_EUR_PER_KILOMETER
          : COMPANY_CAR_EUR_PER_KILOMETER;
      this.updateTravelCost(totalKm, pricePerKilometer);
    }
  };

  updateTotalKilometers = (finalKmState, initialKmState) => {
    return new Promise(resolve => {
      this.setState(
        {
          travelInfoData: {
            ...this.state.travelInfoData,
            totalKilometers: finalKmState - initialKmState
          }
        },
        () => resolve(this.state.travelInfoData.totalKilometers)
      );
    });
  };

  // ---------------------------
  // *** TRAVEL EXPENSE LIST ***

  updateTravelCost = (totalKm, pricePerKm) => {
    this.setState(
      {
        travelInfoData: {
          ...this.state.travelInfoData,
          travelCostValue: Number(totalKm * pricePerKm).toFixed(2)
        }
      },
      () => this.calculateTotalTravelExpense()
    );
  };

  travelExpenseHandleAddRow = () => {
    this.addCardToList("travelExpenseData", createDefaultExpenseCard);
  };

  travelExpenseHandleRemoveSpecificRow = async id => {
    await this.removeCardFromList("travelExpenseData", id);
    this.calculateTotalExpenseWage();
  };

  calculateTotalExpenseWage = () => {
    /*
        TODO: Study why this function is a performance bottleneck.
        Is the problem a slow reduce function?
        */
    const { expenseList } = this.state.travelExpenseData;

    const totalCost = expenseList.reduce(
      (accu, curr) => accu + Number(curr.itemWageAmount),
      0
    );

    this.setState(
      {
        travelExpenseData: {
          ...this.state.travelExpenseData,
          totalWageAmount: Number(totalCost).toFixed(2)
        }
      },
      () => this.calculateTotalTravelExpense()
    );
  };

  travelExpenseToggleCountryDropdown = id => {
    this.setState(prevState => ({
      travelExpenseData: {
        ...prevState.travelExpenseData,
        expenseList: prevState.travelExpenseData.expenseList.map(item =>
          item.id === id
            ? {
                ...item,
                countryDropdownOpen: !item.countryDropdownOpen
              }
            : item
        )
      }
    }));
  };

  travelExpenseChangeCountryValue = (id, e) => {
    const {
      dropdownCountryValue,
      countryWageValue,
      countryShortName
    } = getCountryValuesFromCountriesList(e.currentTarget.textContent);
    this.setState(
      {
        travelExpenseData: {
          ...this.state.travelExpenseData,
          expenseList: this.state.travelExpenseData.expenseList.map(item =>
            item.id === id
              ? {
                  ...item,
                  dropdownCountryValue,
                  countryWageValue
                }
              : item
          )
        }
      },
      () => this.travelExpenseCalculateTotalWage(countryShortName, id)
    );
  };

  travelExpenseToggleAccommodationPaymentDropdown = id => {
    this.setState(prevState => ({
      travelExpenseData: {
        ...prevState.travelExpenseData,
        expenseList: prevState.travelExpenseData.expenseList.map(item =>
          item.id === id
            ? {
                ...item,
                accommodationPaymentDropdownOpen: !item.accommodationPaymentDropdownOpen
              }
            : item
        )
      }
    }));
  };

  travelExpenseChangeAccommodationPaymentValue = (id, e) => {
    const dropdownAccommodationPaymentValue = e.currentTarget.textContent;
    const accommodationPaymentCoefficientValue = Number(e.target.value);
    const expenseData = findExpenseDataCurrentExpenseData(
      id,
      this.state.travelExpenseData.expenseList
    );
    const { countryShortName } = getCountryValuesFromCountriesList(
      expenseData.dropdownCountryValue
    );

    this.setState(
      {
        travelExpenseData: {
          ...this.state.travelExpenseData,
          expenseList: this.state.travelExpenseData.expenseList.map(item =>
            item.id === id
              ? {
                  ...item,
                  dropdownAccommodationPaymentValue,
                  accommodationPaymentCoefficientValue
                }
              : item
          )
        }
      },
      () => this.travelExpenseCalculateTotalWage(countryShortName, id)
    );
  };

  travelExpenseToggleAccommodationTypeDropdown = id => {
    this.setState(prevState => ({
      travelExpenseData: {
        ...prevState.travelExpenseData,
        expenseList: prevState.travelExpenseData.expenseList.map(item =>
          item.id === id
            ? {
                ...item,
                accommodationTypeDropdownOpen: !item.accommodationTypeDropdownOpen
              }
            : item
        )
      }
    }));
  };

  travelExpenseChangeAccommodationTypeValue = (id, e) => {
    const dropdownAccommodationTypeValue = e.currentTarget.textContent;
    const accommodationTypeCoefficientValue = Number(e.target.value);
    const expenseData = findExpenseDataCurrentExpenseData(
      id,
      this.state.travelExpenseData.expenseList
    );
    const { countryShortName } = getCountryValuesFromCountriesList(
      expenseData.dropdownCountryValue
    );

    this.setState(
      {
        travelExpenseData: {
          ...this.state.travelExpenseData,
          expenseList: this.state.travelExpenseData.expenseList.map(item =>
            item.id === id
              ? {
                  ...item,
                  dropdownAccommodationTypeValue,
                  accommodationTypeCoefficientValue
                }
              : item
          )
        }
      },
      () => this.travelExpenseCalculateTotalWage(countryShortName, id)
    );
  };

  travelExpenseHandleChangeDepartureDate = (id, date) => {
    const newDate = moment(date).add(1, "minutes");

    this.setState(
      {
        travelExpenseData: {
          ...this.state.travelExpenseData,
          expenseList: this.state.travelExpenseData.expenseList.map(item =>
            item.id === id
              ? {
                  ...item,
                  wageDepartureDate: date,
                  wageReturnDate: newDate
                }
              : item
          )
        }
      },
      () => this.travelExpenseCalculateTotalHours(id)
    );
  };

  travelExpenseHandleChangeReturnDate = (id, date) => {
    this.setState(
      {
        travelExpenseData: {
          ...this.state.travelExpenseData,
          expenseList: this.state.travelExpenseData.expenseList.map(item =>
            item.id === id
              ? {
                  ...item,
                  wageReturnDate: date
                }
              : item
          )
        }
      },
      () => this.travelExpenseCalculateTotalHours(id)
    );
  };

  travelExpenseCalculateTotalHours = id => {
    const filteredCard = this.state.travelExpenseData.expenseList.filter(
      item => item.id === id
    )[0];

    const { wageDepartureDate, wageReturnDate } = filteredCard;

    const totalHours = Number(
      wageReturnDate.diff(wageDepartureDate) / 1000 / 60 / 60
    ).toFixed(2);

    this.setState(
      {
        travelExpenseData: {
          ...this.state.travelExpenseData,
          expenseList: this.state.travelExpenseData.expenseList.map(item =>
            item.id === id
              ? {
                  ...item,
                  totalHours
                }
              : item
          )
        }
      },
      () => this.travelExpenseCalculateDailyWages(id, totalHours)
    );
  };

  travelExpenseCalculateDailyWages = (id, totalHours) => {
    if (totalHours > 0) {
      const expenseData = findExpenseDataCurrentExpenseData(
        id,
        this.state.travelExpenseData.expenseList
      );
      const { countryShortName } = getCountryValuesFromCountriesList(
        expenseData.dropdownCountryValue
      );

      let overflow = totalHours % 24;
      let fullWages = Math.floor(totalHours / 24);

      if (overflow >= 12) {
        fullWages++;
      } else if (overflow >= 8) {
        fullWages = fullWages + 0.5;
      }

      this.setState(
        {
          travelExpenseData: {
            ...this.state.travelExpenseData,
            expenseList: this.state.travelExpenseData.expenseList.map(item =>
              item.id === id
                ? {
                    ...item,
                    fullWages
                  }
                : item
            )
          }
        },
        () => this.travelExpenseCalculateTotalWage(countryShortName, id)
      );
    }
  };

  travelExpenseHandleInputChange = (id, e) => {
    const inputName = e.target.name;
    const expenseData = findExpenseDataCurrentExpenseData(
      id,
      this.state.travelExpenseData.expenseList
    );

    this.setState(
      {
        travelExpenseData: {
          ...this.state.travelExpenseData,
          expenseList: this.state.travelExpenseData.expenseList.map(item =>
            item.id === id
              ? {
                  ...item,
                  [inputName]: e.target.value
                }
              : item
          )
        }
      },
      () =>
        inputName !== "additionalComment" &&
        this.travelExpenseCalculateTotalWage(
          expenseData.dropdownCountryValue,
          id
        )
    );
  };

  // -------------------------------
  // ****  ADDITIONAL EXPENSES  ****

  travelExpenseCalculateTotalWage = async (countryShortName, id) => {
    let itemWageAmount;

    const filteredCard = this.state.travelExpenseData.expenseList.filter(
      item => item.id === id
    )[0];

    let params = {
      countryShortName: countryShortName,
      accommodationTypeCoefficient:
        filteredCard.accommodationTypeCoefficientValue,
      startDate: filteredCard.wageDepartureDate
        .parseZone()
        .toDate()
        .toISOString(),
      endDate: filteredCard.wageReturnDate
        .parseZone()
        .toDate()
        .toISOString()
    };

    let query = Object.keys(params)
      .map(k => encodeURIComponent(k) + "=" + encodeURIComponent(params[k]))
      .join("&");

    let url = `${ENDPOINT.CALCULATE_WAGE}?` + query;

    await fetchData(url, {
      method: "GET"
    })
      .then(data => data.json())
      .then(res => {
        if (!res.ok) {
          console.log(res);
        }

        itemWageAmount = res.totalAmount;
      });

    this.setState(
      {
        travelExpenseData: {
          ...this.state.travelExpenseData,
          expenseList: this.state.travelExpenseData.expenseList.map(item =>
            item.id === id
              ? {
                  ...item,
                  itemWageAmount
                }
              : item
          )
        }
      },
      () => this.calculateTotalExpenseWage()
    );
  };

  additionalExpenseHandleAddRow = () => {
    this.addCardToList(
      "additionalExpenseData",
      createDefaultAdditionalExpenseCard
    );
  };

  additionalExpenseHandleRemoveSpecificRow = async id => {
    await this.removeCardFromList("additionalExpenseData", id);
    this.calculateTotalAdditionalExpenseWage();
  };

  additionalExpenseHandleChangeDate = (id, date) => {
    this.setState(
      {
        additionalExpenseData: {
          ...this.state.additionalExpenseData,
          expenseList: this.state.additionalExpenseData.expenseList.map(item =>
            item.id === id
              ? {
                  ...item,
                  date
                }
              : item
          )
        }
      },
      () => {
        this.calculateAdditionalExpenseCost(id);
      }
    );
  };

  additionalExpenseHandleInputChange = async (id, e) => {
    const inputName = e.target.name;
    let inputValue = e.target.value;

    if (inputName === "amount" || inputName === "unadmitted") {
      inputValue = stringToNumberFormat(inputValue);
    }

    this.setState(
      {
        additionalExpenseData: {
          ...this.state.additionalExpenseData,
          expenseList: this.state.additionalExpenseData.expenseList.map(item =>
            item.id === id
              ? {
                  ...item,
                  [inputName]: inputValue
                }
              : item
          )
        }
      },
      async () =>
        inputName !== "comment" &&
        inputName !== "type" &&
        this.calculateAdditionalExpenseCost(id)
    );
  };

  additionalExpenseTypeChangeValue = (id, e) => {
    const typeValue = e.currentTarget.textContent;

    this.setState({
      additionalExpenseData: {
        ...this.state.additionalExpenseData,
        expenseList: this.state.additionalExpenseData.expenseList.map(item =>
          item.id === id
            ? {
                ...item,
                type: typeValue
              }
            : item
        )
      }
    });
  };

  additionalExpenseTypeDropdownToggle = id => {
    this.setState(prevState => ({
      additionalExpenseData: {
        ...prevState.additionalExpenseData,
        expenseList: prevState.additionalExpenseData.expenseList.map(item =>
          item.id === id
            ? {
                ...item,
                typeDropdownOpen: !item.typeDropdownOpen
              }
            : item
        )
      }
    }));
  };

  additionalExpenseToggleCurrencyDropdown = id => {
    this.setState(prevState => ({
      additionalExpenseData: {
        ...prevState.additionalExpenseData,
        expenseList: prevState.additionalExpenseData.expenseList.map(item =>
          item.id === id
            ? {
                ...item,
                currencyDropdownOpen: !item.currencyDropdownOpen
              }
            : item
        )
      }
    }));
  };

  additionalExpenseChangeCurrencyValue = (id, e) => {
    const currencyDropdownValue = e.currentTarget.textContent;
    const currencyRate = e.target.value;

    this.setState(
      {
        additionalExpenseData: {
          ...this.state.additionalExpenseData,
          expenseList: this.state.additionalExpenseData.expenseList.map(item =>
            item.id === id
              ? {
                  ...item,
                  currencyDropdownValue,
                  currencyRate
                }
              : item
          )
        }
      },
      () => this.calculateAdditionalExpenseCost(id)
    );
  };

  additionalExpenseTogglePaymentMethodDropdown = id => {
    this.setState(prevState => ({
      additionalExpenseData: {
        ...prevState.additionalExpenseData,
        expenseList: prevState.additionalExpenseData.expenseList.map(item =>
          item.id === id
            ? {
                ...item,
                paymentMethodDropdownOpen: !item.paymentMethodDropdownOpen
              }
            : item
        )
      }
    }));
  };

  additionalExpenseChangePaymentMethodValue = (id, e) => {
    const paymentMethodValue = e.currentTarget.textContent;

    this.setState(
      {
        additionalExpenseData: {
          ...this.state.additionalExpenseData,
          expenseList: this.state.additionalExpenseData.expenseList.map(item =>
            item.id === id
              ? {
                  ...item,
                  paymentMethodValue
                }
              : item
          )
        }
      },
      () => this.resetExistingAdditionalExpenses(id, paymentMethodValue)
    );
  };

  resetExistingAdditionalExpenses = id => {
    this.setState(
      {
        additionalExpenseData: {
          ...this.state.additionalExpenseData,
          expenseList: this.state.additionalExpenseData.expenseList.map(item =>
            item.id === id
              ? {
                  ...item,
                  amount: 0,
                  amountEUR: 0,
                  unadmitted: 0,
                  unadmittedEUR: 0
                }
              : item
          )
        }
      },
      () => this.calculateAdditionalExpenseCost(id)
    );
  };

  calculateAdditionalExpenseCost = async id => {
    const filteredCard = this.state.additionalExpenseData.expenseList.filter(
      item => item.id === id
    )[0];

    const { amount, unadmitted } = filteredCard;

    let params = {
      amount: unadmitted ? unadmitted : amount,
      currency: filteredCard.currencyDropdownValue,
      date: filteredCard.date.format("YYYY-MM-DD")
    };

    let query = Object.keys(params)
      .map(k => encodeURIComponent(k) + "=" + encodeURIComponent(params[k]))
      .join("&");
    let url = `${ENDPOINT.CALCULATE_TRAVEL_EXPENSES}?` + query;
    let currencyRate;
    let totalExpenseAmountEUR;
    await fetchData(url, {
      method: "GET"
    })
      .then(data => data.json())
      .then(text => {
        totalExpenseAmountEUR = text.totalAmount;
        currencyRate = text.rate;
      })
      .catch(function(error) {
        console.log("request failed", error);
      });
    this.setState(
      {
        additionalExpenseData: {
          ...this.state.additionalExpenseData,
          expenseList: this.state.additionalExpenseData.expenseList.map(item =>
            item.id === id
              ? {
                  ...item,
                  currencyRate,
                  unadmittedEUR: unadmitted ? totalExpenseAmountEUR : 0,
                  amountEUR: unadmitted ? 0 : totalExpenseAmountEUR
                }
              : item
          )
        }
      },
      () => this.calculateTotalAdditionalExpenseWage()
    );
  };

  calculateTotalAdditionalExpenseWage = () => {
    const { expenseList } = this.state.additionalExpenseData;

    const additionalExpenses = expenseList.reduce(
      (acc, curr) => {
        const admitted = Number(curr.amountEUR).toFixed(2);
        const unadmitted = Number(curr.unadmittedEUR).toFixed(2);

        return {
          totalCost:
            Number(acc.totalCost) + Number(admitted) + Number(unadmitted),
          totalUnadmitted: Number(acc.totalUnadmitted) + Number(unadmitted)
        };
      },
      {
        totalCost: 0,
        totalUnadmitted: 0
      }
    );

    this.setState(
      {
        additionalExpenseData: {
          ...this.state.additionalExpenseData,
          totalAdmittedAmount: additionalExpenses.totalCost,
          totalUnadmittedAmount: additionalExpenses.totalUnadmitted
        }
      },
      () => this.calculateTotalTravelExpense()
    );
  };

  calculateTotalTravelExpense = () => {
    const {
      travelExpenseData: { totalWageAmount },
      travelInfoData: { travelCostValue },
      additionalExpenseData: { totalAdmittedAmount }
    } = this.state;

    const totalTravelExpenseAmount = +(
      Number(travelCostValue) +
      Number(totalWageAmount) +
      Number(totalAdmittedAmount)
    ).toFixed(2);

    this.setState(
      {
        totalTravelExpenseAmount
      },
      () => this.calculatePaymentAmount()
    );
  };

  // *** VALIDATION FUNCTIONS (create, edit form) ***

  calculatePaymentAmount = () => {
    const {
      totalTravelExpenseAmount,
      travelInfoData: { downPayment },
      additionalExpenseData: { totalUnadmittedAmount }
    } = this.state;

    const paymentAmount =
      totalTravelExpenseAmount - Number(downPayment) - totalUnadmittedAmount;

    this.setState({
      paymentAmount
    });
  };

  shouldGenerateDocument = () =>
    this.validateNecessaryFieldsForGeneratingDocument();

  validateNecessaryFieldsForGeneratingDocument = () =>
    !!this.state.travelIdData.orderNumber &&
    !!this.state.personalData.firstName &&
    !!this.state.personalData.lastName &&
    !!this.state.personalData.profession &&
    !!this.state.personalData.workingPosition &&
    !!this.state.travelInfoData.destinationCity &&
    !!this.state.travelInfoData.destinationCountry &&
    !!this.state.travelInfoData.tripPurposeGroup;

  // -------------------------
  // *** UTILITY FUNCTIONS ***

  validateFieldsForCreatingInitialRecord = () =>
    !!this.state.personalData.firstName &&
    !!this.state.personalData.lastName &&
    !!this.state.personalData.profession &&
    !!this.state.personalData.workingPosition &&
    !!this.state.travelInfoData.destinationCity &&
    !!this.state.travelInfoData.destinationCountry;

  initCurrency = () => {
    if (!this.state.additionalExpenseData.expenseList.length) {
      return;
    }

    const id = this.state.additionalExpenseData.expenseList[0].id;
    const curDate = moment(new Date()).format("YYYY-MM-DD");
    this.getCurrencyRates(curDate, id);
  };

  adjustCurrencyList(arr) {
    const defaultObject = {
      drzava: "Hrvatska",
      valuta: "EUR",
      srednji_tecaj: "1"
    };

    const priorityArr = [defaultObject, ...arr].filter(
      y =>
        y.valuta.includes("EUR") ||
        y.valuta.includes("USD") ||
        y.valuta.includes("GBP")
    );

    const restArr = arr
      .filter(y => !priorityArr.some(z => z.valuta === y.valuta))
      .sort((a, b) => {
        if (a.valuta > b.valuta) {
          return 1;
        } else if (a.valuta < b.valuta) {
          return -1;
        }
        return 0;
      });

    return [...priorityArr, ...restArr].map((y, i) => ({
      id: i + 1,
      name: y.valuta
    }));
  }

  addCardToList = (list, cardCreatorFn) => {
    this.setState({
      [list]: {
        ...this.state[list],
        expenseList: [...this.state[list].expenseList, cardCreatorFn(uuid.v4())]
      }
    });
  };
  // *** NEW METHODS FOR CALLING BACKEND  ***

  removeCardFromList = (list, cardID) => {
    const expenseList = this.state[list].expenseList.filter(
      card => card.id !== cardID
    );

    return new Promise((resolve, reject) => {
      this.setState(
        {
          [list]: {
            ...this.state[list],
            expenseList
          }
        },
        () => resolve()
      );
    });
  };

  getCurrencyRates = (date, id) => {
    fetchData(`${ENDPOINT.CURRENCY_RATES}/${date}`, {
      method: "GET"
    })
      .then(res => {
        if (!res.ok) {
          throw new Error();
        }
        return res.json();
      })
      .then(currencyList =>
        this.setState(
          {
            ...this.state,
            additionalExpenseData: {
              ...this.state.additionalExpenseData,
              currencyRatesList: this.adjustCurrencyList(currencyList)
            }
          },
          () => {
            const expense = this.state.additionalExpenseData.expenseList.find(
              x => x.id === id
            );
            const currencyRate = this.state.additionalExpenseData.currencyRatesList.find(
              x => expense.currencyDropdownValue === x.name
            );
          }
        )
      )
      .catch(_ => {
        if (!isLoggedIn()) {
          return;
        }
        this.setState({ error: "Error ocurred while fetching currencies" });
      });
  };

  createTrip = () => {
    if (!this.validateFieldsForCreatingInitialRecord()) {
      this.setState({ error: "Fill all required data (*)" });
      return;
    }

    fetchData(ENDPOINT.TRAVEL_EXPENSES, {
      method: "POST",
      body: JSON.stringify({
        travelIdData: {
          yearDropDownValue: this.state.travelIdData.yearDropDownValue
        },
        personalData: {
          firstName: this.state.personalData.firstName,
          lastName: this.state.personalData.lastName,
          profession: this.state.personalData.profession,
          workingPosition: this.state.personalData.workingPosition
        },
        travelInfoData: { ...this.state.travelInfoData }
      })
    })
      .then(res => res.json())
      .then(res => {
        if (!!res.status && res.status !== 200) {
          throw new Error(res.message);
        }
        window.location.href = "#" + URL.getTravelExpense;
      })
      .catch(err => this.setState({ error: err.message }));
  };

  pdfOnDownloadClickCallback = (year, orderNumber) => {
    const timeZone = Intl.DateTimeFormat()
      .resolvedOptions()
      .timeZone.replace("/", "-");

    fetchData(
      `${ENDPOINT.TRAVEL_EXPENSES}/${year}/${orderNumber}/${timeZone}/generatePdf`,
      {
        method: "GET"
      }
    )
      .then(res => {
        if (!res.ok) {
          throw new Error();
        }
        return res.blob();
      })
      .then(blob => createPdfFile(blob, orderNumber))
      .catch(_ => this.setState({ error: "Could not generate PDF" }));
  };

  fetchAllExpenses = () => {
    fetchData(`${ENDPOINT.TRAVEL_EXPENSES}`, {
      method: "GET"
    })
      .then(res => {
        if (!res.ok) {
          throw new Error();
        }
        return res.json();
      })
      .then(data =>
        this.setState({ travelRecords: data, travelRecordsCopy: data })
      )
      .catch(_ =>
        this.setState({ error: "Error ocurred, could not get Travel expenses" })
      );
  };

  // EDIT TRAVEL EXPENSES
  editRecord = (year, orderNumber) => {
    fetchData(`${ENDPOINT.TRAVEL_EXPENSES}/${year}/${orderNumber}`, {
      method: "GET"
    })
      .then(res => {
        if (!res.ok) {
          throw new Error();
        }
        return res.json();
      })
      .then(data => this.transferStateToEditPage(data))
      .catch(_ => this.setState({ error: "Unable to edit record" }));
  };

  deleteRecord = (year, orderNumber) => {
    fetchData(`${ENDPOINT.TRAVEL_EXPENSES}/${year}/${orderNumber}`, {
      method: "DELETE"
    })
      .then(res => {
        if (!res.ok) {
          throw new Error();
        }
        this.setState({
          travelRecords: this.state.travelRecords.filter(
            x => x.orderNumber !== orderNumber
          ),
          travelRecordsCopy: this.state.travelRecordsCopy.filter(
            x => x.orderNumber !== orderNumber
          )
        });
      })
      .catch(_ => this.setState({ error: "Unable to delete record" }));
  };

  // ** ADMIN METHODS **

  saveEditedTrip = (year, orderNumber) => {
    fetchData(`${ENDPOINT.TRAVEL_EXPENSES}/${year}/${orderNumber}`, {
      method: "POST",
      body: JSON.stringify(this.state)
    })
      .then(res => {
        if (!res.ok) {
          throw new Error();
        }
        return res;
      })
      .then(_ => this.setState({ documentSaved: true }))
      .catch(_ =>
        this.setState({ error: "Error ocurred, could not save Travel warrant" })
      );
  };

  getAllUsers = () => {
    fetchData(ENDPOINT.GET_ALL_USERS, {
      method: "GET"
    })
      .then(res => {
        if (!res.ok) {
          throw new Error();
        }
        return res.json();
      })
      .then(x => this.setState({ users: x }))
      .catch(_ => this.setState({ error: "Error while fetching users" }));
  };

  addAdminRole = email => {
    fetchData(ENDPOINT.SET_ADMIN_RIGHT, {
      method: "POST",
      body: email
    })
      .then(x => {
        if (x.status !== 200) {
          throw new Error();
        }
        // add ROLE_ADMIN to array
        this.setState({
          users: this.state.users.map(x =>
            x.email === email
              ? {
                  ...x,
                  authorities: [...x.authorities, ROLES.ADMIN]
                }
              : x
          )
        });
      })
      .catch(_ => this.setState({ error: "Error while adding admin role" }));
  };

  removeAdminRole = email => {
    fetchData(ENDPOINT.REMOVE_ADMIN_RIGHT, {
      method: "POST",
      body: email
    })
      .then(x => {
        if (x.status !== 200) {
          throw new Error();
        }
        // remove ROLE_ADMIN from array
        this.setState({
          users: this.state.users.map(x =>
            x.email === email
              ? {
                  ...x,
                  authorities: x.authorities.filter(y => y === ROLES.USER)
                }
              : x
          )
        });
      })
      .catch(_ => this.setState({ error: "Error while removing admin role" }));
  };

  transferStateToEditPage = x => {
    this.setState(
      {
        id: x.id,
        purposeOfTrip: x.purposeOfTrip,
        totalUnadmittedAmountTemp: x.totalUnadmittedAmountTemp,
        totalTravelExpenseAmount: x.totalTravelExpenseAmount,
        paymentAmount: x.paymentAmount,
        travelIdData: {
          orderNumber: x.travelIdData.orderNumber,
          yearDropDownValue: x.travelIdData.yearDropDownValue
        },
        personalData: {
          ...x.personalData,
          id: x.personalData.id,
          firstName: x.personalData.firstName,
          lastName: x.personalData.lastName,
          profession: x.personalData.profession,
          workingPosition: x.personalData.workingPosition
        },
        travelInfoData: {
          ...x.travelInfoData,
          duration: x.travelInfoData.duration,
          departureDate: arrDateToDateObjInLocalTime(
            x.travelInfoData.departureDate
          ),
          returnDate: arrDateToDateObjInLocalTime(x.travelInfoData.returnDate),
          downPayment: x.travelInfoData.downPayment,
          isTripCanceled: x.travelInfoData.isTripCanceled
        },
        travelExpenseData: {
          expenseList: !x.travelExpenseData
            ? [
                {
                  ...createDefaultExpenseCard(uuid.v4()),
                  wageDepartureDate: arrDateToDateObjInLocalTime(
                    x.travelInfoData.departureDate
                  ),
                  wageReturnDate: arrDateToDateObjInLocalTime(
                    x.travelInfoData.departureDate
                  )
                }
              ]
            : x.travelExpenseData.expenseList.map(y => ({
                ...y,
                wageDepartureDate: arrDateToDateObjInLocalTime(
                  y.wageDepartureDate
                ),
                wageReturnDate: !y.wageReturnDate
                  ? arrDateToDateObjInLocalTime(y.wageDepartureDate).add(
                      1,
                      "minute"
                    )
                  : arrDateToDateObjInLocalTime(y.wageReturnDate),
                accommodationTypeCoefficientValue:
                  ACCOMMODATION_TYPE[0].coefficient
              })),
          totalWageAmount: !!x.travelExpenseData
            ? x.travelExpenseData.totalWageAmount
            : 0
        },
        additionalExpenseData: {
          expenseList: !x.additionalExpenseData
            ? [createDefaultAdditionalExpenseCard(uuid.v4())]
            : x.additionalExpenseData.expenseList.map(y => ({
                ...y,
                date: arrDateToDateObjInLocalTime(y.date),
                paymentMethodsList: PAYMENT_METHODS
              })),

          currencyRatesList: [],
          totalAdmittedAmount: !x.additionalExpenseData
            ? 0
            : x.additionalExpenseData.totalAdmittedAmount,
          totalUnadmittedAmount: !x.additionalExpenseData
            ? 0
            : x.additionalExpenseData.totalUnadmittedAmount
        },
        hasFile: !!x.fileKey,
        orderStatus: x.orderStatus
      },
      () => (window.location.href = "#" + URL.editTravelExpense)
    );
  };

  // ** GET TRAVEL EXPENSES SORT AND FILTER **
  filterUsersByName = value =>
    this.setState({
      travelRecords: this.state.travelRecordsCopy.filter(
        x => x.lastName.toLowerCase().indexOf(value.toLowerCase()) > -1
      )
    });

  sortData = (key, flag) => {
    this.setState(
      {
        travelRecords: this.state.travelRecords.sort((x, y) => {
          let a = x[key];
          let b = y[key];
          if (key === "departureDate") {
            a = arrDateToDateObjInLocalTime(a);
            b = arrDateToDateObjInLocalTime(b);
          }
          if (a < b) {
            return flag;
          } else if (a > b) {
            return flag * -1;
          }
          return 0;
        })
      },
      () =>
        this.setState({
          orderFlags: {
            ...this.state.orderFlags,
            [key]: this.state.orderFlags[key] * -1
          }
        })
    );
  };

  // ---------------------------
  // *** DATA FOR CONTEXT API ***

  generateContextAPI = () => ({
    state: this.state,
    callbackList: this.callbackList()
  });

  callbackList = () => ({
    travelIdToggleYearDropdown: this.travelIdToggleYearDropdown,
    travelIdChangeYearValue: this.travelIdChangeYearValue,
    personalDataHandleInputChange: this.personalDataHandleInputChange,
    travelInfoHandleDepartureDateChange: this
      .travelInfoHandleDepartureDateChange,
    travelInfoHandleReturnDateChange: this.travelInfoHandleReturnDateChange,
    travelInfoHandleInputChange: this.travelInfoHandleInputChange,
    travelInfoToggleVehicleDropdown: this.travelInfoToggleVehicleDropdown,
    travelInfoChangeVehicleValue: this.travelInfoChangeVehicleValue,
    travelInfoTripPurposeGroupChangeValue: this
      .travelInfoTripPurposeGroupChangeValue,
    travelInfoTripPurposeGroupDropdownToggle: this
      .travelInfoTripPurposeGroupDropdownToggle,
    travelInfoCancelTrip: this.travelInfoCancelTrip,
    carTravelInfoHandleInputChange: this.carTravelInfoHandleInputChange,
    carTravelInfoHandleKilometersChange: this
      .carTravelInfoHandleKilometersChange,
    travelExpenseHandleAddRow: this.travelExpenseHandleAddRow,
    travelExpenseHandleRemoveSpecificRow: this
      .travelExpenseHandleRemoveSpecificRow,
    travelExpenseToggleCountryDropdown: this.travelExpenseToggleCountryDropdown,
    travelExpenseChangeCountryValue: this.travelExpenseChangeCountryValue,
    travelExpenseToggleAccommodationPaymentDropdown: this
      .travelExpenseToggleAccommodationPaymentDropdown,
    travelExpenseChangeAccommodationPaymentValue: this
      .travelExpenseChangeAccommodationPaymentValue,
    travelExpenseToggleAccommodationTypeDropdown: this
      .travelExpenseToggleAccommodationTypeDropdown,
    travelExpenseChangeAccommodationTypeValue: this
      .travelExpenseChangeAccommodationTypeValue,
    travelExpenseHandleChangeDepartureDate: this
      .travelExpenseHandleChangeDepartureDate,
    travelExpenseHandleChangeReturnDate: this
      .travelExpenseHandleChangeReturnDate,
    travelExpenseHandleInputChange: this.travelExpenseHandleInputChange,
    additionalExpenseHandleAddRow: this.additionalExpenseHandleAddRow,
    additionalExpenseHandleRemoveSpecificRow: this
      .additionalExpenseHandleRemoveSpecificRow,
    additionalExpenseToggleCurrencyDropdown: this
      .additionalExpenseToggleCurrencyDropdown,
    additionalExpenseChangeCurrencyValue: this
      .additionalExpenseChangeCurrencyValue,
    additionalExpenseTogglePaymentMethodDropdown: this
      .additionalExpenseTogglePaymentMethodDropdown,
    additionalExpenseChangePaymentMethodValue: this
      .additionalExpenseChangePaymentMethodValue,
    additionalExpenseHandleChangeDate: this.additionalExpenseHandleChangeDate,
    additionalExpenseHandleInputChange: this.additionalExpenseHandleInputChange,
    additionalExpenseTypeChangeValue: this.additionalExpenseTypeChangeValue,
    additionalExpenseTypeDropdownToggle: this
      .additionalExpenseTypeDropdownToggle,
    pdfOnDownloadClickCallback: this.pdfOnDownloadClickCallback,
    shouldGenerateDocument: this.shouldGenerateDocument,
    fetchAllExpenses: this.fetchAllExpenses,
    createTrip: this.createTrip,
    deleteRecord: this.deleteRecord,
    editRecord: this.editRecord,
    saveEditedTrip: this.saveEditedTrip,
    initCurrency: this.initCurrency,
    getAllUsers: this.getAllUsers,
    filterUsersByName: this.filterUsersByName,
    addAdminRole: this.addAdminRole,
    removeAdminRole: this.removeAdminRole,
    sortData: this.sortData,
    setTravelExpenses: this.setTravelExpenses,
    setHasFile: this.setHasFile
  });

  render() {
    return (
      <AppContext.Provider value={this.generateContextAPI()}>
        {!!this.state.error && (
          <Alert color="danger text-center">{this.state.error}</Alert>
        )}
        {this.state.documentSaved && (
          <Alert color="success text-center">Successfully saved</Alert>
        )}
        <Switch>
          {routes.map((route, idx) =>
            route.private ? (
              <PrivateRoute
                key={idx}
                path={route.path}
                exact={route.exact}
                name={route.name}
                component={route.component}
              />
            ) : (
              <Route
                key={idx}
                path={route.path}
                exact={route.exact}
                name={route.name}
                render={props => <route.component {...props} />}
              />
            )
          )}
        </Switch>
      </AppContext.Provider>
    );
  }
}

export default withRouter(GlobalStateWrapper);
